import { useState } from "react";
import { api } from "@/lib/leuteliaApi";
import { Monogram } from "./Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Check, Loader2, X, MapPin, Globe, Building2 } from "lucide-react";

export default function Onboarding({ onDone, onClose }) {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [url, setUrl] = useState("");
  const [ext, setExt] = useState({});
  const [showExt, setShowExt] = useState(false);
  const [phase, setPhase] = useState("form"); // form | scanning
  const [sources, setSources] = useState([]);
  const [revealed, setRevealed] = useState(0);
  const [error, setError] = useState(null);

  const start = async () => {
    if (!name.trim() || !city.trim()) { setError("Nome e città sono obbligatori."); return; }
    setError(null);
    setPhase("scanning");
    try {
      const job = await api.onboard({ name: name.trim(), city: city.trim(), url: url.trim(),
        booking_url: ext.booking || null, expedia_url: ext.expedia || null, agoda_url: ext.agoda || null, tripcom_url: ext.tripcom || null, tripadvisor_url: ext.tripadvisor || null, facebook_url: ext.facebook || null, instagram_url: ext.instagram || null });
      let data = null;
      for (let i = 0; i < 90 && !data; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = await api.onboardStatus(job.job_id);
        if (st.status === "done") data = st.result;
        else if (st.status === "error") throw new Error(st.error || "Scansione non riuscita");
      }
      if (!data) throw new Error("La scansione sta richiedendo più del previsto: riprova tra un minuto.");
      setSources(data.scan_report.sources);
      // rivela le fonti una a una per l'effetto "scansione in corso"
      for (let i = 1; i <= data.scan_report.sources.length; i++) {
        await new Promise((r) => setTimeout(r, 650));
        setRevealed(i);
      }
      await new Promise((r) => setTimeout(r, 700));
      onDone(data.scope);
    } catch (e) {
      setError(e.message);
      setPhase("form");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-green/95 backdrop-blur-sm flex items-center justify-center p-6 leutelia-grain">
      <div className="relative w-full max-w-xl bg-brand-cream rounded-2xl shadow-2xl overflow-hidden leu-in">
        <button data-testid="onboarding-close" onClick={onClose} className="absolute top-4 right-4 text-brand-muted hover:text-brand-ink">
          <X size={20} />
        </button>
        <div className="px-9 pt-9 pb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center">
              <Monogram size={20} a="#F5F1E8" b="#C6902A" />
            </div>
            <div>
              <div className="text-brand-orange font-semibold tracking-[0.22em] text-[11px]">NUOVO HOTEL</div>
              <h2 className="font-serif text-2xl text-brand-ink leading-tight">Aggiungi il tuo hotel</h2>
            </div>
          </div>

          {phase === "form" && (
            <div className="space-y-4">
              <p className="text-[14px] text-brand-muted leading-relaxed">
                Inserisci nome, città e sito. LEUTELIA avvia subito una <strong>prima scansione di base</strong> della
                presenza online — Google, sito, recensioni, social e OTA — e consolida la comprensione dell'hotel.
              </p>
              <Field icon={Building2} label="Nome hotel" value={name} onChange={setName} placeholder="Es. Hotel Belvedere" testid="onboarding-name" />
              <Field icon={MapPin} label="Città" value={city} onChange={setCity} placeholder="Es. Como, Italia" testid="onboarding-city" />
              <Field icon={Globe} label="Sito ufficiale (URL)" value={url} onChange={setUrl} placeholder="https://…" testid="onboarding-url" />
              <button type="button" data-testid="onboarding-toggle-ext" onClick={() => setShowExt((v) => !v)} className="text-[12px] font-semibold text-brand-green hover:underline">
                {showExt ? "Nascondi" : "Aggiungi"} link OTA e social (opzionali: Booking, Expedia, Facebook, Instagram)
              </button>
              {showExt && (
                <div className="grid grid-cols-2 gap-3">
                  {[["booking", "Booking.com"], ["expedia", "Expedia"], ["agoda", "Agoda"], ["tripcom", "Trip.com"], ["tripadvisor", "TripAdvisor"], ["facebook", "Facebook"], ["instagram", "Instagram"]].map(([k, l]) => (
                    <Input key={k} data-testid={`onboarding-${k}`} placeholder={`URL ${l}`} value={ext[k] || ""} onChange={(e) => setExt((x) => ({ ...x, [k]: e.target.value }))} className="h-10 bg-white border-brand-border rounded-lg text-[13px]" />
                  ))}
                </div>
              )}
              {error && <div className="text-[13px] text-brand-maroon">{error}</div>}
              <Button data-testid="onboarding-submit" onClick={start} className="w-full h-11 bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[14px] font-semibold rounded-lg">
                <Search size={16} className="mr-2" /> Avvia scansione di base
              </Button>
            </div>
          )}

          {phase === "scanning" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[13px] text-brand-green font-semibold mb-2">
                <Loader2 size={15} className="animate-spin text-brand-gold" />
                Scansione della presenza online in corso…
              </div>
              {(sources.length ? sources : PLACEHOLDER).map((s, i) => {
                const done = i < revealed;
                return (
                  <div key={i} className={`flex items-center justify-between rounded-lg border px-4 py-3 bg-white transition-all duration-300 ${done ? "border-brand-green/30" : "border-brand-border"}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? "bg-brand-green" : "bg-brand-cream"}`}>
                        {done ? <Check size={14} className="text-brand-cream" /> : <span className="w-2 h-2 rounded-full bg-brand-gold leu-scanning" />}
                      </span>
                      <span className="text-[14px] text-brand-ink font-medium">{s.label}</span>
                      {done && s.live !== undefined && (
                        <span data-testid={`scan-source-${s.key}-${s.live ? "live" : "mock"}`} className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold tracking-wide ${s.live ? "bg-emerald-100 text-emerald-700" : "bg-brand-cream text-brand-muted border border-brand-gold/30"}`}>
                          {s.live ? "LIVE" : "MOCK"}
                        </span>
                      )}
                    </div>
                    <span className="text-[12px] text-brand-muted font-mono truncate max-w-[220px]">{done ? (s.detail || "trovato") : "…"}</span>
                  </div>
                );
              })}
              <p className="text-[11px] text-brand-muted pt-1 italic">
                Google via Places API quando configurata (LIVE); le altre fonti sono simulate (MOCK) finché non arrivano gli scanner reali.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PLACEHOLDER = [
  { label: "Google (scheda + recensioni)" }, { label: "Sito ufficiale" }, { label: "Immagini hotel" },
  { label: "Booking.com" }, { label: "Expedia" }, { label: "Facebook" }, { label: "Instagram" },
];

function Field({ icon: Icon, label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold tracking-wide text-brand-muted uppercase">{label}</span>
      <div className="relative mt-1.5">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gold" />
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="pl-9 h-11 bg-white border-brand-border rounded-lg text-[14px]" />
      </div>
    </label>
  );
}
